import { forwardRef } from 'react';
import { keyframes } from '@mui/material';
import Box from '@mui/material/Box';

import { TestIds } from '@watershed/shared-universal/utils/testUtils';
import { sharedFocusShadow, sharedShadow } from '@watershed/style/styleUtils';
import AILogo from '@watershed/ui-core/components/AILogo';
import Button from '@watershed/ui-core/components/Button';
import { useFeatureFlag } from '../../../utils/FeatureFlag';
import { Flags } from '@watershed/constants/flags';

export const TRIGGER_POSITION = 16;

interface AIChatSidebarTriggerProps {
  onClick: (event: React.MouseEvent<HTMLElement>) => void;
  isOpen: boolean;
}

const pulseAnimation = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.08);
  }
  100% {
    transform: scale(1);
  }
`;

export const AIChatSidebarTrigger = forwardRef<
  HTMLButtonElement,
  AIChatSidebarTriggerProps
>(function AIChatSidebarTrigger({ onClick, isOpen }, ref) {
  const isCustomerAIEnabled = useFeatureFlag(Flags.CustomerAIEnabled);

  if (!isCustomerAIEnabled) {
    return null;
  }

  return (
    <Box
      data-testid={TestIds.AITrigger}
      sx={{
        position: 'fixed',
        bottom: TRIGGER_POSITION,
        right: TRIGGER_POSITION,
        zIndex: (theme) => theme.zIndex.drawer + 1,
      }}
    >
      <Button
        ref={ref}
        onClick={onClick}
        variant="contained"
        sx={{
          gap: 1,
          height: 'fit-content',
          minWidth: 'fit-content',
          p: 1,
          borderRadius: '100px',
          border: (theme) => `0.5px solid ${theme.palette.grey40}`,
          boxShadow: sharedShadow,
          animation: `${pulseAnimation} 2s infinite`, // Add the pulse animation
          '&:hover': {
            backgroundColor: (theme) => theme.palette.grey05,
            boxShadow: sharedShadow,
            animation: `${pulseAnimation} 2s infinite`, // Add the pulse animation
          },
          '&.active, &:focus': {
            backgroundColor: (theme) => theme.palette.white,
            border: (theme) => `0.5px solid ${theme.palette.cobalt50}`,
            boxShadow: sharedFocusShadow,
            animation: 'none', // Stop the animation when active/focused
          },
        }}
      >
        <AILogo size={20} />
      </Button>
    </Box>
  );
});
